import React, { useCallback } from 'react';
import { View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import moment from 'moment';

import {
	DAY_OF_WEEK_SHORT_LABEL,
	PEOPLE_EMPLOYMENT_TYPE,
	PEOPLE_EMPLOYMENT_TYPE_NAMES,
	PEOPLE_PAY_RATE_FIELD,
	PEOPLE_PAY_RATE_FIELD_NAMES,
	PEOPLE_TEAM_NAMES,
} from '../../../common/constants';
import { formatCurrency } from '../../../common/utils/utils';
import { GET_MY_EMPLOYEE_PROFILE_MOBILE } from '../../graphql/people';
import { Text } from '../../components';
import { Card, EmptyState, Screen, SecondaryButton, SectionTitle } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const formatMaybeDate = value => (value ? moment(value).format('D MMM YYYY') : '');

const formatWorkDays = (workDays) => {
	if (!Array.isArray(workDays) || !workDays.length) {
		return '';
	}

	return workDays
		.map(day => DAY_OF_WEEK_SHORT_LABEL[day] || day)
		.filter(Boolean)
		.join(', ');
};

const getEmployeeName = (employee) => {
	if (!employee) {
		return '';
	}

	return [employee.preferredName || employee.firstName, employee.lastName]
		.filter(Boolean)
		.join(' ')
		.trim();
};

const hasValue = row => Boolean(row.value) || row.value === 0;

// Card of label/value rows; rows without a value are left out, a card with
// none is not shown, and the last row has no divider under it.
const DetailCard = ({ title, rows }) => {
	const styles = useThemedStyles(createStyles);
	const visibleRows = rows.filter(hasValue);

	if (!visibleRows.length) {
		return null;
	}

	return (
		<Card>
			<SectionTitle title={title} style={styles.cardTitle} />
			{visibleRows.map((row, index) => (
				<View key={row.label} style={[styles.detailRow, index === visibleRows.length - 1 && styles.detailRowLast]}>
					<Text style={styles.detailLabel}>{row.label}</Text>
					<Text style={styles.detailValue}>{row.value}</Text>
				</View>
			))}
		</Card>
	);
};

// Read-only employment summary for the signed-in employee, mirroring the People app
// My account Employment tab (sensitive payroll fields stay people-app only).
const MyEmploymentView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);

	const { data, loading } = useQuery(GET_MY_EMPLOYEE_PROFILE_MOBILE, {
		fetchPolicy: 'cache-and-network',
	});

	const employee = data?.getMyEmployeeProfile || null;

	const openPositionDescription = useCallback(() => {
		navigation.navigate('MyDocumentViewerView', {
			document: employee?.positionDescriptionDocument,
		});
	}, [employee?.positionDescriptionDocument, navigation]);

	if (!employee) {
		return (
			<Screen
				backLabel="My account"
				heading="Employment"
				isLoading={loading}
			>
				<EmptyState
					icon="briefcase"
					title="No employee profile"
					message="No employee profile is linked to this account. Employment details are available once you have an employee profile."
				/>
			</Screen>
		);
	}

	const employmentType = Number(employee.employmentType);
	const isCasual = employmentType === PEOPLE_EMPLOYMENT_TYPE.CASUAL;
	const isPartTime = employmentType === PEOPLE_EMPLOYMENT_TYPE.PART_TIME;
	const isFullTime = employmentType === PEOPLE_EMPLOYMENT_TYPE.FULL_TIME;
	const isContractor = employmentType === PEOPLE_EMPLOYMENT_TYPE.CONTRACTOR;
	const originalStartDate = employee.originalStartDate || employee.startDate;
	const showOriginalStartDate = Boolean(originalStartDate &&
		formatMaybeDate(originalStartDate) !== formatMaybeDate(employee.startDate));

	return (
		<Screen
			backLabel="My account"
			heading="Employment"
			subheading="Your current position and pay details. Contact your manager if something is out of date."
		>
			<DetailCard
				title="Work details"
				rows={[
					{ label: 'State', value: employee.workTenant?.name },
					{ label: 'Position', value: employee.position?.name },
					{ label: 'Team', value: PEOPLE_TEAM_NAMES[employee.team] },
					{ label: 'Reporting to', value: getEmployeeName(employee.managerEmployee) },
					{ label: 'Start date', value: formatMaybeDate(employee.startDate) },
					showOriginalStartDate && { label: 'Original start date', value: formatMaybeDate(originalStartDate) },
					{ label: 'Probation end date', value: formatMaybeDate(employee.probationEndDate) },
					{ label: 'Work email', value: employee.workEmail },
				].filter(Boolean)}
			/>

			<DetailCard
				title="Pay details"
				rows={[
					{ label: 'Employment type', value: PEOPLE_EMPLOYMENT_TYPE_NAMES[employmentType] },
					isPartTime && { label: 'Work days', value: formatWorkDays(employee.workDays) },
					(isFullTime || isPartTime) && { label: 'Hours per week', value: employee.hoursPerWeek },
					{ label: 'Award', value: employee.awardName },
					...(isCasual
						? [
							{ label: 'Pay rate level', value: employee.pay?.payRateProfileName },
							{
								label: PEOPLE_PAY_RATE_FIELD_NAMES[PEOPLE_PAY_RATE_FIELD.WEEKDAY],
								value: formatCurrency(employee.pay?.[PEOPLE_PAY_RATE_FIELD.WEEKDAY]),
							},
						]
						: [{ label: isContractor ? 'Remuneration' : 'Salary', value: formatCurrency(employee.salary) }]),
				].filter(Boolean)}
			/>

			{
				employee.positionDescriptionDocument ? (
					<Card>
						<SectionTitle title="Position description" />
						<Text style={styles.mutedText}>
							{employee.positionDescriptionDocument.title || 'Position description'}
						</Text>
						<SecondaryButton
							label="View description"
							icon="file-text"
							onPress={openPositionDescription}
							style={styles.cardButton}
						/>
					</Card>
				) : null
			}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space, type } }) => ({
	cardTitle: {
		marginBottom: space.xs,
	},
	cardButton: {
		alignSelf: 'flex-start',
		marginTop: space.md,
	},
	detailRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		gap: space.md,
		paddingVertical: 10,
		borderBottomWidth: 1,
		borderBottomColor: colors.border,
	},
	detailRowLast: {
		borderBottomWidth: 0,
		paddingBottom: 0,
	},
	detailLabel: {
		...type.secondary,
		color: colors.textSecondary,
	},
	detailValue: {
		...type.bodySmallSemibold,
		color: colors.text,
		flexShrink: 1,
		textAlign: 'right',
	},
	mutedText: {
		...type.secondary,
		color: colors.textSecondary,
		marginTop: space.xs,
	},
});

export default MyEmploymentView;
